import React, { useEffect, useState } from 'react';
import { Alert, Button, Col, Form, Input, message, Row, Select, Switch } from 'antd';
import { ExportOutlined } from '@ant-design/icons';
import styles from './jiraSettings.module.css';
import { get, post } from '../../utils/fetch';
import { useIntegrationConnection } from './useIntegrationConnection';

type OptionType = { value: string; label: string };

interface Props {
  projectId: string;
  organizationId: string;
  authProviders?: string[];
  integration:
    | {
        enabled: boolean;
        id: string;
        settings?: {
          project?: OptionType;
          task_list?: OptionType;
          workflow_status?: OptionType;
          new_tasks?: boolean;
          sync_task_updates?: boolean;
          new_comments?: boolean;
          sync_productive_comments?: boolean;
          sync_productive_status?: boolean;
          task_status?: OptionType;
          status_mapping?: Record<string, string>;
        };
      }
    | undefined;
  onDisconnect?: () => void;
}

const ProductiveSettings: React.FC<Props> = ({
  projectId,
  organizationId,
  authProviders = [],
  integration,
  onDisconnect
}) => {
  const [form] = Form.useForm();
  const [isSavingAuth, setIsSavingAuth] = useState(false);
  const [isProjectsLoading, setIsProjectsLoading] = useState(false);
  const [isTaskListsLoading, setIsTaskListsLoading] = useState(false);
  const [isWorkflowStatusesLoading, setIsWorkflowStatusesLoading] = useState(false);
  const [selectedProject, setSelectedProject] = useState<OptionType | undefined>(
    integration?.settings?.project
  );
  const [selectedTaskList, setSelectedTaskList] = useState<OptionType | undefined>(
    integration?.settings?.task_list
  );
  const [selectedWorkflowStatus, setSelectedWorkflowStatus] = useState<OptionType | undefined>(
    integration?.settings?.workflow_status
  );
  const [selectedStatus, setSelectedStatus] = useState<OptionType | undefined>(
    integration?.settings?.task_status
  );
  const [autoPost, setAutoPost] = useState(
    integration?.settings?.new_tasks ?? false
  );
  const [syncTaskUpdates, setSyncTaskUpdates] = useState(
    integration?.settings?.sync_task_updates ?? true
  );
  const [newComments, setNewComments] = useState(
    integration?.settings?.new_comments ?? false
  );
  const [syncProductiveComments, setSyncProductiveComments] = useState(
    integration?.settings?.sync_productive_comments ?? false
  );
  const [syncProductiveStatus, setSyncProductiveStatus] = useState(
    integration?.settings?.sync_productive_status ?? false
  );
  const [statusMappings, setStatusMappings] = useState<
    Record<string, string>
  >({});
  const [projectOptions, setProjectOptions] = useState<OptionType[]>([]);
  const [taskListOptions, setTaskListOptions] = useState<OptionType[]>([]);
  const [workflowStatusOptions, setWorkflowStatusOptions] = useState<OptionType[]>([]);
  const [statusOptions, setStatusOptions] = useState<OptionType[]>([]);
  const [isStatusLoading, setIsStatusLoading] = useState(false);

  const invertStatusMappings = (mapping: Record<string, string> = {}) =>
    Object.entries(mapping).reduce(
      (acc, [productiveStatusId, bugherdColumnId]) => {
        acc[bugherdColumnId] = productiveStatusId;
        return acc;
      },
      {} as Record<string, string>
    );

  const hasProductiveAuth =
    authProviders.includes('productive') || !!integration?.enabled;
  const connectionIntegration =
    integration || (hasProductiveAuth ? { enabled: true } : undefined);
  const {
    integrationId,
    projectIntegrationId,
    getConnectedState,
    saveSettings,
    disconnect
  } = useIntegrationConnection({
    organizationId,
    projectId,
    integration: connectionIntegration,
    integrationCode: 'Productive',
    onDisconnect,
    onConnectedStateLoaded: results => {
        const project = results.settings?.project || integration?.settings?.project;
        const taskList = results.settings?.task_list || integration?.settings?.task_list;
        const workflowStatus =
          results.settings?.workflow_status || integration?.settings?.workflow_status;
        const taskStatus = results.settings?.task_status || integration?.settings?.task_status;
        setSelectedProject(project);
        setSelectedTaskList(taskList);
        setSelectedWorkflowStatus(workflowStatus);
        setSelectedStatus(taskStatus);
        setAutoPost(results.settings?.new_tasks ?? integration?.settings?.new_tasks ?? false);
        setSyncTaskUpdates(
          results.settings?.sync_task_updates ??
            integration?.settings?.sync_task_updates ??
            true
        );
        setNewComments(results.settings?.new_comments ?? integration?.settings?.new_comments ?? false);
        setSyncProductiveComments(
          results.settings?.sync_productive_comments ??
            integration?.settings?.sync_productive_comments ??
            false
        );
        setSyncProductiveStatus(
          results.settings?.sync_productive_status ??
            integration?.settings?.sync_productive_status ??
            false
        );
        setStatusMappings(
          invertStatusMappings(
            results.settings?.status_mapping ||
              integration?.settings?.status_mapping ||
              {}
          )
        );
        form.setFieldsValue({
          project: project?.value,
          taskList: taskList?.value,
          workflowStatus: workflowStatus?.value,
          statusColumn: taskStatus?.value
        });
    }
  });

  const saveAuth = async (values: {
    productive_organization_id: string;
    auth_token: string;
  }) => {
    setIsSavingAuth(true);
    try {
      await post(
        `/organizations/${organizationId}/integrations/productive_auth`,
        {
          project_id: projectId,
          productive_organization_id: values.productive_organization_id,
          auth_token: values.auth_token
        }
      );
      message.success('Productive connected.');
      window.location.href = `${window.location.pathname}?open_integration=productive`;
    } catch (error) {
      message.error('Failed to connect Productive. Please check your API token and organization ID.');
    } finally {
      setIsSavingAuth(false);
    }
  };

  const errorMessage = (error: unknown, fallback: string) =>
    (error as Error)?.message || fallback;

  const getProjects = async () => {
    if (!canLoadProductiveData) return;

    setIsProjectsLoading(true);
    try {
      const results = await get(
        `/organizations/${organizationId}/integrations/remote_projects?id=${integrationId}`
      );
      setProjectOptions(results);
    } catch (error) {
      message.error(
        errorMessage(error, 'Failed to fetch Productive projects. Please try again.')
      );
    } finally {
      setIsProjectsLoading(false);
    }
  };

  const getTaskLists = async (productiveProjectId: string) => {
    if (!canLoadProductiveData || !productiveProjectId) return;

    setIsTaskListsLoading(true);
    try {
      const results = await get(
        `/organizations/${organizationId}/integrations/remote_projects?id=${integrationId}&site_name=task_lists:${productiveProjectId}`
      );
      setTaskListOptions(results);
    } catch (error) {
      message.error(
        errorMessage(error, 'Failed to fetch Productive task lists. Please try again.')
      );
    } finally {
      setIsTaskListsLoading(false);
    }
  };

  const getWorkflowStatuses = async (productiveProjectId: string) => {
    if (!canLoadProductiveData || !productiveProjectId) return;

    setIsWorkflowStatusesLoading(true);
    try {
      const results = await get(
        `/organizations/${organizationId}/integrations/remote_projects?id=${integrationId}&site_name=workflow_statuses:${productiveProjectId}`
      );
      setWorkflowStatusOptions(results);
    } catch (error) {
      message.error(
        errorMessage(error, 'Failed to fetch Productive workflow statuses. Please try again.')
      );
    } finally {
      setIsWorkflowStatusesLoading(false);
    }
  };

  const getStatusColumns = async () => {
    if (!hasProductiveAuth) return;

    setIsStatusLoading(true);
    try {
      const results = await get(`/projects/full?id=${projectId}`);
      const columns: OptionType[] = results.columns.map(
        ({ id, name }: { id: string; name: string }) => ({
          value: String(id),
          label: name
        })
      );
      setStatusOptions(columns);
    } catch (error) {
      message.error('Failed to fetch status columns. Please try again.');
    } finally {
      setIsStatusLoading(false);
    }
  };
  const canLoadProductiveData = hasProductiveAuth && !!integrationId;

  const handleProjectChange = (
    _value: string | undefined,
    option: OptionType | OptionType[] | undefined
  ) => {
    const next = Array.isArray(option) ? option[0] : option;
    setSelectedProject(next);
    setSelectedTaskList(undefined);
    setTaskListOptions([]);
    setSelectedWorkflowStatus(undefined);
    setWorkflowStatusOptions([]);
    setStatusMappings({});
    form.setFieldsValue({
      taskList: undefined,
      workflowStatus: undefined
    });
  };

  const handleTaskListChange = (
    _value: string | undefined,
    option: OptionType | OptionType[] | undefined
  ) => {
    const next = Array.isArray(option) ? option[0] : option;
    setSelectedTaskList(next);
  };

  const handleWorkflowStatusChange = (
    _value: string | undefined,
    option: OptionType | OptionType[] | undefined
  ) => {
    const next = Array.isArray(option) ? option[0] : option;
    setSelectedWorkflowStatus(next);
  };

  const handleStatusChange = (
    _value: string | undefined,
    option: OptionType | OptionType[] | undefined
  ) => {
    const next = Array.isArray(option) ? option[0] : option;
    setSelectedStatus(next);
  };

  const handleStatusMappingChange = (
    bugherdColumnId: string,
    productiveStatusId?: string
  ) => {
    setStatusMappings({
      ...statusMappings,
      [bugherdColumnId]: productiveStatusId || ''
    });
  };

  const handleSubmit = () => {
    const validBugherdColumnIds = statusOptions.map(option => option.value);
    const hasLoadedStatuses = validBugherdColumnIds.length > 0;
    const cleanedStatusMappings = Object.keys(statusMappings).reduce(
      (acc, bugherdColumnId) => {
        const productiveStatusId = statusMappings[bugherdColumnId];
        if (!productiveStatusId) return acc;
        if (
          hasLoadedStatuses &&
          !validBugherdColumnIds.includes(bugherdColumnId)
        )
          return acc;
        acc[productiveStatusId] = bugherdColumnId;
        return acc;
      },
      {} as Record<string, string>
    );

    return saveSettings(
      {
        project: selectedProject,
        task_list: selectedTaskList,
        workflow_status: selectedWorkflowStatus,
        new_tasks: autoPost,
        sync_task_updates: syncTaskUpdates,
        new_comments: newComments,
        sync_productive_comments: syncProductiveComments,
        sync_productive_status: syncProductiveStatus,
        task_status: selectedStatus || {},
        status_mapping: cleanedStatusMappings
      },
      { form }
    );
  };

  useEffect(() => {
    if (hasProductiveAuth) {
      getConnectedState({ errorMessage: false });
      getStatusColumns();
    }
  }, [integration, projectId, organizationId, hasProductiveAuth]);

  useEffect(() => {
    if (canLoadProductiveData) {
      getProjects();
    }
  }, [integrationId, projectId, organizationId, hasProductiveAuth]);

  useEffect(() => {
    if (selectedProject?.value) {
      getTaskLists(selectedProject.value);
      getWorkflowStatuses(selectedProject.value);
    }
  }, [selectedProject?.value]);

  const renderStatusMappings = () => (
    <div className={styles.statusMappings}>
      <Row className={styles.statusMappingRow} gutter={16}>
        <Col span={11}>
          <strong>BugHerd Status</strong>
        </Col>
        <Col span={2}></Col>
        <Col span={11}>
          <strong>Productive Workflow Status</strong>
        </Col>
      </Row>
      {statusOptions.map(bugherdStatus => (
        <Row
          key={bugherdStatus.value}
          className={styles.statusMappingRow}
          gutter={16}
          align="middle"
        >
          <Col span={11}>
            <span className={styles.jiraStatusLabel}>
              {bugherdStatus.label}
            </span>
          </Col>
          <Col span={2} style={{ textAlign: 'center' }}>
            <span>-</span>
          </Col>
          <Col span={11}>
            <Select
              className={styles.bugherdColumnSelect}
              placeholder="Select workflow status"
              value={statusMappings[bugherdStatus.value] || undefined}
              onChange={value =>
                handleStatusMappingChange(bugherdStatus.value, value)
              }
              options={workflowStatusOptions}
              loading={isWorkflowStatusesLoading}
              disabled={!selectedProject?.value}
              optionFilterProp="label"
              showSearch
              allowClear
            />
          </Col>
        </Row>
      ))}
    </div>
  );

  if (!hasProductiveAuth) {
    return (
      <div className={styles.jiraExplanation}>
        <p>Connect BugHerd with your Productive account to sync tasks and comments.</p>
        <Form form={form} layout="vertical" onFinish={saveAuth} requiredMark={false}>
          <Form.Item
            label="Organization ID"
            name="productive_organization_id"
            rules={[{ required: true, message: 'Enter your Productive organization ID.' }]}
          >
            <Input autoComplete="off" />
          </Form.Item>
          <Form.Item
            label="API token"
            name="auth_token"
            rules={[{ required: true, message: 'Enter your Productive API token.' }]}
          >
            <Input.Password autoComplete="off" />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={isSavingAuth}>
            Connect
          </Button>
        </Form>
      </div>
    );
  }

  return (
    <div>
      <Form
        form={form}
        layout="horizontal"
        onFinish={handleSubmit}
        autoComplete="off"
        requiredMark={false}
      >
        <div className={styles.jiraRequired}>
          <div className={styles.requiredBubble}>REQUIRED</div>
          <h3>Connect your Productive destination</h3>
          <Row className={styles.jiraEditRow}>
            <Col span={24}>
              <Form.Item
                name="project"
                label={
                  <span className={styles.requiredLabel}>
                    <span>*</span>Project
                  </span>
                }
                className={styles.jiraProject}
                rules={[
                  {
                    required: true,
                    message: 'Please select a Productive project.'
                  }
                ]}
                initialValue={selectedProject?.value}
              >
                <Select
                  className={styles.jiraSelect}
                  showSearch
                  placeholder="Select a project"
                  optionFilterProp="label"
                  options={projectOptions}
                  onChange={handleProjectChange}
                  loading={isProjectsLoading}
                  allowClear
                />
              </Form.Item>
            </Col>
          </Row>
          <Row className={styles.jiraEditRow}>
            <Col span={24}>
              <Form.Item
                name="taskList"
                label={
                  <span className={styles.requiredLabel}>
                    <span>*</span>Task List
                  </span>
                }
                className={styles.jiraProject}
                rules={[
                  {
                    required: true,
                    message: 'Please select a Productive task list.'
                  }
                ]}
                initialValue={selectedTaskList?.value}
              >
                <Select
                  className={styles.jiraSelect}
                  showSearch
                  placeholder="Select a task list"
                  optionFilterProp="label"
                  options={taskListOptions}
                  onChange={handleTaskListChange}
                  loading={isTaskListsLoading}
                  disabled={!selectedProject?.value}
                  allowClear
                />
              </Form.Item>
            </Col>
          </Row>
          <Row className={styles.jiraEditRow}>
            <Col span={24}>
              <Form.Item
                name="workflowStatus"
                label={
                  <span className={styles.requiredLabel}>
                    <span>*</span>Workflow Status
                  </span>
                }
                className={styles.jiraProject}
                rules={[
                  {
                    required: true,
                    message: 'Please select a Productive workflow status.'
                  }
                ]}
                initialValue={selectedWorkflowStatus?.value}
              >
                <Select
                  className={styles.jiraSelect}
                  showSearch
                  placeholder="Select a workflow status"
                  optionFilterProp="label"
                  options={workflowStatusOptions}
                  onChange={handleWorkflowStatusChange}
                  loading={isWorkflowStatusesLoading}
                  disabled={!selectedProject?.value}
                  allowClear
                />
              </Form.Item>
            </Col>
          </Row>
        </div>

        <div className={styles.jiraWorkflows}>
          <h3>Choose your desired workflow</h3>
          <Alert
            className={styles.productivePlanNotice}
            type="info"
            showIcon
            message={
              <span>
                Two-way sync is only available to Productive Ultimate customers.{' '}
                <a
                  href="https://productive.io/pricing/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View Productive pricing.
                </a>
              </span>
            }
          />
          <Row className={styles.jiraEditRow}>
            <Col span={24}>
              <Form.Item
                label="Automatically post new tasks to Productive"
                className={styles.autoPost}
              >
                <Switch checked={autoPost} onChange={e => setAutoPost(e)} />
              </Form.Item>
            </Col>
            {autoPost && (
              <Col span={24}>
                <Form.Item
                  name="statusColumn"
                  label="Create task when status is equal to"
                  className={styles.selectStatus}
                  initialValue={selectedStatus?.value}
                  tooltip="This is an optional field. Select a status to trigger task creation in Productive."
                >
                  <Select
                    placeholder="Select a status"
                    options={statusOptions}
                    onChange={handleStatusChange}
                    loading={isStatusLoading}
                    allowClear
                  />
                </Form.Item>
              </Col>
            )}
            <Col span={24}>
              <Form.Item label="Automatically sync task detail updates to Productive">
                <Switch
                  checked={syncTaskUpdates}
                  onChange={setSyncTaskUpdates}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row className={styles.jiraEditRow}>
            <Col span={24}>
              <Form.Item
                className={styles.newComments}
                label="Automatically send new BugHerd comments to Productive"
              >
                <Switch
                  checked={newComments}
                  onChange={e => setNewComments(e)}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row className={styles.jiraEditRow}>
            <Col span={24}>
              <Form.Item
                className={styles.syncComments}
                label="Sync comments from Productive to BugHerd"
              >
                <Switch
                  checked={syncProductiveComments}
                  onChange={e => setSyncProductiveComments(e)}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row className={styles.jiraEditRow}>
            <Col span={24}>
              <Form.Item
                className={styles.syncStatus}
                label="Sync status from Productive to BugHerd"
                tooltip="This option is only available on the Productive Ultimate plan."
              >
                <Switch
                  checked={syncProductiveStatus}
                  onChange={e => setSyncProductiveStatus(e)}
                />
              </Form.Item>
            </Col>
          </Row>
          {syncProductiveStatus && selectedProject?.value && (
            <Row className={styles.jiraEditRow}>
              <Col span={24}>{renderStatusMappings()}</Col>
            </Row>
          )}
          <Row className={styles.jiraEditRow}>
            <Col span={24}>
              <Form.Item
                className={styles.syncUsers}
                label="Sync users between Productive and BugHerd"
              >
                {projectIntegrationId && selectedProject?.value ? (
                  <a
                    href={`/settings/organizations/${organizationId}/team?manage_productive_users=true&integration_id=${integrationId}&project_integration_id=${projectIntegrationId}&productive_project_id=${selectedProject.value}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.manageLink}
                  >
                    Manage <ExportOutlined style={{ marginLeft: '5px' }} />
                  </a>
                ) : (
                  <span>
                    Save a Productive project before managing user mappings.
                  </span>
                )}
              </Form.Item>
            </Col>
          </Row>
        </div>

        <Row className={styles.jiraEditRow} justify="space-between" align="middle">
          <Col>
            <Form.Item style={{ marginBottom: 0 }}>
              <Button
                type="primary"
                shape="round"
                htmlType="submit"
                className={styles.save}
              >
                Save
              </Button>
            </Form.Item>
          </Col>
          <Col>
            <Button
              type="text"
              shape="round"
              onClick={() => disconnect({ deleteFallback: true })}
              className={styles.disconnectButton}
              danger
            >
              Disconnect
            </Button>
          </Col>
        </Row>
      </Form>
    </div>
  );
};

export default ProductiveSettings;
